import React, { useEffect } from 'react';
import { Button, Card, Typography, Space, Modal } from "antd";
import useDateRange from '@/hooks/use-date-range';
import { safemomentmax } from '../UtilityData/New-Summary/utils';
import moment from 'moment';
import { useSummaryData } from '../UtilityData/New-Summary/hooks/useSummaryData';
import { useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
import { api } from '@/redux/api';
import { DashboardOutlined } from '@ant-design/icons';
import UpdateEuiTable from './certify-new/update-eui-table';
import { EventName, useEventBus } from '@/hooks/useEventBus';

const { Title } = Typography;

const UpdateEUI = ({ updateEUI, task, isCertified, visible, currentState }) => {

    const dateRange = useDateRange();
    const facilityId = useSelector(state => state.Facility.selected.Id);
    const currentUserId = useSelector(state => state.Auth.userId);

    let endingMonth = moment(safemomentmax(
        moment(dateRange.date_range_elec_max),
        moment(dateRange.date_range_gas_max)
    )).format("YYYY-MM");

    const {data: benchmarkData, run: refreshBenchmark} = useRequest(() => {
        return api.getBenchmarkByFacilityId(facilityId);
    });

    useEventBus(EventName.REFRESH_CERTIFY_REQUEST, () => {
        refreshBenchmark();
        refreshElectricity();
        refreshGas();
    });

    useEventBus(EventName.UPDATE_CERTIFICATION_STATUS, () => {
        refreshElectricity();
        refreshGas();
    });

    // 每次抽屉打开时，强制刷新所有数据
    useEffect(() => {
        if (visible) {
            refreshBenchmark();
            refreshElectricity();
            refreshGas();
        }
    }, [visible]);

    const {data: currentSelectedFacility ={}} = useRequest(() => {
        return api.getCurrentFacility().then(res => {
            if(res.status === 200) {
                return res.data;
            }
        });
    });

    const {data: facilityCategoryOptions = []} = useRequest(() => {
        return api.getFacilityCategory(true).then(res => {
            if(res.status === 200) {
                // 现在接口不再分页，直接返回数组
                return Array.isArray(res.data) ? res.data : (res.data?.results || []);
            }
        });
    });

    // 根据 facility_category ID 获取对应的名称
    const getFacilityCategoryName = (categoryId) => {
        if (!categoryId) return '-';
        const category = facilityCategoryOptions.find(item => item.id === categoryId);
        return category?.name || categoryId;
    };
    const {
        data: electricityData = {
          electricity: [],
        },
        refresh: refreshElectricity
      } = useSummaryData("electricity", '12', endingMonth);

      const {
        data: gasData = {
          gas: [],
        },
        refresh: refreshGas
      } = useSummaryData("gas", '12', endingMonth);

      const newData = {
        "total_eui": Math.round((electricityData?.annual_eui || 0) + (gasData?.annual_eui || 0)),
        "eui_elec": Math.round((electricityData?.annual_eui || 0)),
        "eui_ng": Math.round((gasData?.annual_eui || 0)),
        "total_costs": (electricityData.total_cost + gasData.total_cost),
        "costs_per_sqft": (gasData.total_area_cost + electricityData.total_area_cost),
        facility: currentSelectedFacility?.name,
        region: currentSelectedFacility?.region,
        year: currentSelectedFacility?.year,
        company: currentSelectedFacility?.company_name,
        company_id: currentSelectedFacility?.company,
        facility_id: currentSelectedFacility?.id,
        city: currentSelectedFacility?.city,
        state: currentSelectedFacility?.state,
        country: currentSelectedFacility?.country,
        size: currentSelectedFacility?.area,
        facility_category: currentSelectedFacility?.facility_category,
        climate_zone: currentSelectedFacility?.climate_zone,
      }
      const oldData = {
        "total_eui": benchmarkData?.eui ?? '-', // EUI (kBtu/q-yr)
        "eui_elec": benchmarkData?.eui_elec ?? '-', // EUI - Elec(kBtu/sq-yr)
        "eui_ng": benchmarkData?.eui_ng ?? '-', // EUI - NG (kBtu/sq-yr)
        "total_costs": benchmarkData?.energy_costs ?? '-', // Energy Costs ($)
        "costs_per_sqft": benchmarkData?.energy_costs_sqft ?? '-', // Energy Costs ($/sqft)
        facility: benchmarkData?.facility, // Facility Name
        region: benchmarkData?.region,
        year: benchmarkData?.year,
        company: benchmarkData?.company,
        city: benchmarkData?.city,
        state: benchmarkData?.state,
        country: benchmarkData?.country,
        size: benchmarkData?.size,
        facility_category: benchmarkData?.facility_category_name,
        climate_zone: benchmarkData?.eui_climate_zoneng,
      }
    return (
        <div>
            <UpdateEuiTable 
                oldData={oldData}
                newData={newData}
                getFacilityCategoryName={getFacilityCategoryName}
                isCertified={isCertified}
            />

            {/* Update Button - Only show in IN_PROGRESS state (step 4) */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                {task?.assigned_to === currentUserId && currentState === 'IN_PROGRESS' && (
                    <Button 
                        type="primary" 
                        size="middle" 
                        onClick={() => {
                            // Check if benchmark data exists (assuming id indicates existence)
                            const hasBenchmarkData = benchmarkData && benchmarkData.id;

                            if (!hasBenchmarkData && !isCertified) {
                                Modal.error({
                                    title: 'Certification Incomplete',
                                    content: 'No previous benchmark data exist. Complete all data certification (Electricity & Gas) before updating.',
                                });
                                return;
                            }

                            // Prepare data based on certification status
                            let finalData = { ...newData };
                            if (!isCertified) {
                                // If not certified, remove EUI related fields
                                delete finalData.total_eui;
                                delete finalData.eui_elec;
                                delete finalData.eui_ng;
                            }

                            Modal.confirm({
                                title: 'Confirm Update',
                                content: !isCertified 
                                    ? 'Note: Some utility data is not certified. EUI values (Total EUI, Elec EUI, Gas EUI) will not be updated. Ohter facility data will be updated.'
                                    : 'Update current facility data into the Benchmark database.',
                                okText: 'Update',
                                cancelText: 'Cancel',
                                onOk: () => {
                                    updateEUI(finalData);
                                },
                                onCancel: () => {
                                    // 取消时不执行任何操作
                                }
                            });
                        }}
                        style={{
                            height: '38px',
                            padding: '0 24px',
                            borderRadius: '20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            boxShadow: '0 3px 10px rgba(102, 126, 234, 0.4)'
                        }}
                    >
                        Update
                    </Button>
                )}
            </div>
        </div>
    )
}

export default UpdateEUI;

